const { Buffer } = require('buffer');
const { formatPermissions } = require('./utils');
const { datapointsPermissions } = require('./datapoints');
const { dataplusPermissions } = require('./dataplus');
const { wurPermissions } = require('./wur');
const { wurReporterInterimPermissions } = require('./wurReporterInterim');
const { submissionPermissions } = require('./submission');
const { explorePermissions } = require('./explore');
const { authPermissions } = require('./auth');
const { legacyPermissions } = require('./legacy');
const { sdgImpactPermissions } = require('./sdgImpact');
const { reputationPermissions } = require('./reputation');
const { reputationPlusPermissions } = require('./reputationPlus');
const {
  japanRankingsDashboardPermissions,
} = require('./japanRankingsDashboard');
const { dataplusNewDataPermissions } = require('./dataplusNewData');
const { dataplusLatamPermissions } = require('./dataplusLatam');
const { cidPermissions } = require('./cid');
const {
  collegeRankingsDashboardPermissions,
} = require('./collegeRankingsDashboard');
const { bundlePermissions } = require('./bundle');
const { reputationCoreNewPermissions } = require('./reputationCoreNew');
const { reputationAdvancedNewPermissions } = require('./reputationAdvancedNew');
const { impactBrandKitPermissions } = require('./impactBrandKit');
const { theInsightsPermissions } = require('./theInsights')

const sdgImpactRoleNames = [
  'SDG_IMPACT_SUBSCRIPTION_PREMIUM',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED',
  'SDG_IMPACT_SUBSCRIPTION_BASIC',
];

const sdgImpactFullRoles = [
  'SDG_IMPACT_SUBSCRIPTION_PREMIUM',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_1',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_2',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_3',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_4',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_5',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_6',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_7',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_8',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_9',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_10',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_11',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_12',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_13',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_14',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_15',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_16',
  'SDG_IMPACT_SUBSCRIPTION_ADVANCED_17',
  'SDG_IMPACT_SUBSCRIPTION_BASIC',
];

const sdgIdNumbers = [
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
];

const sdgIdsStrings = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
];

const getSDGNumbers = ({
  permissionToken,
  permissionList,
  institutionId,
  updatedLegacyPermissions,
}) => {
  const decodedToken = JSON.parse(
    Buffer.from(permissionToken.split('.')[1], 'base64').toString(),
  );

  const { permissions, isAdmin } = decodedToken;
  const formattedPermissions = formatPermissions(permissions, permissionList);

  if (
    formattedPermissions[institutionId].some((item) =>
      item.includes(`SDG_IMPACT_SUBSCRIPTION_PREMIUM`),
    ) ||
    isAdmin ||
    (permissions && permissions['*'] && permissions['*'].length)
  ) {
    // If user has Premium subscription return all 17 SDGs
    return sdgIdNumbers;
  }

  const regex = /SDG_IMPACT_SUBSCRIPTION_ADVANCED_(\d+)$/;

  const extractedNumbers = [];
  formattedPermissions[institutionId].forEach((permission) => {
    const match = permission.match(regex);

    if (match && match[1]) {
      // Convert the captured string to an integer and push it to the result array
      const numberValue = parseInt(match[1], 10);
      extractedNumbers.push(numberValue);
    }
  });

  if (extractedNumbers.length) return extractedNumbers;

  if (
    formattedPermissions[institutionId].some((item) =>
      item.includes(`SDG_IMPACT_SUBSCRIPTION_BASIC`),
    )
  ) {
    return [];
  }

  if (updatedLegacyPermissions.releases?.impactSdgs.length) {
    return updatedLegacyPermissions.releases?.impactSdgs;
  }

  return [];
};

const getFirstRoleOfUser = (
  rolesToCheck,
  permissionToken,
  permissionList,
  institutionId,
) => {
  const decodedToken = JSON.parse(
    Buffer.from(permissionToken.split('.')[1], 'base64').toString(),
  );

  const { permissions } = decodedToken;
  const formattedPermissions = formatPermissions(permissions, permissionList);

  return (
    rolesToCheck.find((role) =>
      formattedPermissions[institutionId].some((permission) =>
        permission.includes(role),
      ),
    ) ?? null
  );
};

const checkPermissions = ({
  permissionToken = null,
  permissionList = null,
  institutionId = '',
  action = '',
  year = 0,
  sdgId = 0,
  availableImpactYears = [],
  isDuringEmbargo = false,
}) => {
  if (!permissionToken || !permissionList || !institutionId || !action) {
    return false;
  }

  const decodedToken = JSON.parse(
    Buffer.from(permissionToken.split('.')[1], 'base64').toString(),
  );

  const { permissions, isAdmin } = decodedToken;

  if (isAdmin) {
    return true;
  }

  if (permissions && permissions['*'] && permissions['*'].length) {
    return true;
  }

  const formattedPermissions = formatPermissions(permissions, permissionList);

  const product = action.split(':')[0];

  if (!formattedPermissions[institutionId]) {
    formattedPermissions[institutionId] = ['DATAPOINTS_USER'];
  }

  if (formattedPermissions[institutionId].includes(`DATAPOINTS_DEVELOPER`)) {
    return true;
  }

  if (
    action.endsWith(':viewPage') &&
    formattedPermissions[institutionId].includes(
      `THE_STAFF_ACCESS_ALL_INSTITUTIONS`,
    )
  ) {
    return true;
  }

  if (product === 'datapoints') {
    return datapointsPermissions(action, formattedPermissions, institutionId);
  }

  if (product === 'dataplus') {
    return dataplusPermissions(
      action,
      formattedPermissions,
      institutionId,
      year,
    );
  }

  if (product === 'wur') {
    return wurPermissions(action, formattedPermissions[institutionId], year);
  }

  if (product === 'wurReporterInterim') {
    return wurReporterInterimPermissions(
      action,
      formattedPermissions[institutionId],
      year,
    );
  }

  if (product === 'submission') {
    return submissionPermissions(
      action,
      formattedPermissions,
      institutionId,
      year,
    );
  }

  if (product === 'explore') {
    return explorePermissions(
      action,
      formattedPermissions[institutionId],
      year,
    );
  }

  if (product === 'auth') {
    return authPermissions(action, formattedPermissions, institutionId);
  }

  if (product === 'legacy') {
    return legacyPermissions(
      action,
      formattedPermissions,
      institutionId,
      year,
      sdgId,
    );
  }

  if (product === 'sdgImpact') {
    return sdgImpactPermissions(
      action,
      formattedPermissions,
      institutionId,
      year,
      sdgId,
      availableImpactYears,
      isDuringEmbargo,
    );
  }

  if (product === 'reputation') {
    return reputationPermissions(
      action,
      formattedPermissions,
      institutionId,
      year,
    );
  }

  if (product === 'reputation_plus') {
    return reputationPlusPermissions(
      action,
      formattedPermissions,
      institutionId,
      year,
    );
  }

  if (product === 'reputation_core_new') {
    return reputationCoreNewPermissions(
      action,
      formattedPermissions,
      institutionId,
    );
  }

  if (product === 'reputation_advanced_new') {
    return reputationAdvancedNewPermissions(
      action,
      formattedPermissions,
      institutionId,
    );
  }

  if (product === 'japanRankingsDashboard') {
    return japanRankingsDashboardPermissions(
      action,
      formattedPermissions,
      institutionId,
      year,
    );
  }

  if (product === 'dataplusNewData') {
    return dataplusNewDataPermissions(
      action,
      formattedPermissions,
      institutionId,
      year,
    );
  }

  if (product === 'dataplusLatam') {
    return dataplusLatamPermissions(
      action,
      formattedPermissions,
      institutionId,
      year,
    );
  }

  if (product === 'cid') {
    return cidPermissions(action, formattedPermissions, institutionId, year);
  }

  if (product === 'collegeRankingsDashboard') {
    return collegeRankingsDashboardPermissions(
      action,
      formattedPermissions,
      institutionId,
      year,
    );
  }

  if (product === 'bundle') {
    return bundlePermissions(action, formattedPermissions, institutionId);
  }

  if (product === 'impactBrandKit') {
    return impactBrandKitPermissions(
      action,
      formattedPermissions,
      institutionId,
    );
  }

  if (product === 'the_insights') {
    return theInsightsPermissions(
      action,
      formattedPermissions,
      institutionId,
    );
  }

  return false;
};

module.exports = {
  sdgImpactRoleNames,
  sdgIdNumbers,
  sdgIdsStrings,
  sdgImpactFullRoles,
  checkPermissions,
  getSDGNumbers,
  getFirstRoleOfUser,
};
